/**
 * Everything standing between the reader and editing this module.
 *
 * @module components/studio/ModuleNotices
 */

import React from "react";

import { rebaseOnto } from "@/components/studio/module-conflict-fields";
import { ModuleConflictPanel } from "@/components/studio/ModuleConflictPanel";
import type { ModuleEditorState } from "@/components/studio/useModuleEditor";
import {
  Alert,
  Button,
  EditLockProvider,
  type EditRefusal,
  Heading,
  MutedText,
  Panel,
  Time,
  useConfirm,
} from "@/components/ui";
import { editorAvailability } from "@/router/programme-editor/editor-availability";
import { isWorkflowEditable } from "@/server/workflow";

/** A refusal with only a full sentence: Module Studio has no header chip to fill. */
function refusal(full: string): EditRefusal {
  return { full };
}

/**
 * The edit lease's part in refusing edits: the module is reloading, its lease
 * was refused, it went back to its builder, or the lease is still being taken.
 * Null when the lease stands in nothing's way.
 */
export function moduleLeaseRefusal(editor: ModuleEditorState): EditRefusal | null {
  const { record, leaseError } = editor;
  if (editor.viewOnly) {
    return null;
  }
  if (editor.reloading) {
    return refusal("Wait for the module to reload.");
  }
  if (leaseError) {
    // The fallbacks raised on this side carry no full stop; the server's do.
    const cause = leaseError.message.trim();
    return refusal(`${/[.!?]$/.test(cause) ? cause : `${cause}.`} This module is open read only.`);
  }
  if (!record?.canEdit || editor.canEdit) {
    return null;
  }
  // A lease still being taken locks too: a record kept from an earlier visit
  // says nothing yet about who holds the module now.
  return editor.handedBack
    ? refusal("This module was returned to its builder, so you cannot edit it.")
    : refusal("Waiting for this module's edit lease.");
}

/**
 * Whether the module's content can be edited at all, and why not. A cause it
 * shares with programme sections takes their wording, so the two surfaces
 * give one reason. Null when it can.
 */
export function moduleEditDecision(editor: ModuleEditorState): EditRefusal | null {
  const { record } = editor;
  // In the sections' order: the workflow, then opening to view. A change case
  // opens on its own status, which the record reports instead.
  const shared = editorAvailability({
    readOnly: false,
    workflowState: record && !editor.changeCaseId ? record.workflowState : undefined,
    canEditWorkflowState: !record || isWorkflowEditable(record.workflowState),
    canEditProgramme: true,
    viewing: editor.viewOnly,
  });
  if (shared) {
    return shared;
  }
  if (!record) {
    return refusal("This module is open read only.");
  }
  if (record.canEdit) {
    return null;
  }
  const { status } = record.moduleReview;
  if (status === "Submitted for Review" || status === "Approved") {
    return refusal("Editing is unavailable while this module is under review or approved.");
  }
  if (record.visibilityState === "locked") {
    return refusal("This programme is locked, so its modules are read only.");
  }
  return refusal("You can view this module but not edit it.");
}

/** Why the module is read only, first the lease and then the content's own reason. */
export function readOnlyReason(editor: ModuleEditorState): string {
  return (
    (moduleLeaseRefusal(editor) ?? moduleEditDecision(editor))?.full ??
    "This module is open read only."
  );
}

/** Named once: both controls of the retry pair wait on the same save. */
const SAVING = "Wait for the save in progress to finish.";

/** Why the retry pair refuses, which the save in flight answers first. */
function retryRefusal(editor: ModuleEditorState): string | undefined {
  if (editor.saveStatus === "saving") {
    return SAVING;
  }
  if (!editor.canEdit) {
    return "This module is open read only.";
  }
  return editor.dirty ? undefined : "Nothing is waiting to be saved.";
}

export function ModuleNotices({
  editor,
}: {
  editor: ModuleEditorState;
}): React.ReactElement | null {
  const confirm = useConfirm();
  const { record, moduleDraft } = editor;
  if (!record || !moduleDraft) {
    return null;
  }
  const review = record.moduleReview;

  const askToDiscard = (): Promise<boolean> =>
    confirm({
      title: "Discard your unsaved changes?",
      body: "The module reloads from the last save. Anything you typed since then is lost.",
      confirmLabel: "Discard and reload",
      tone: "danger",
      "data-testid": "module-discard-confirmation",
    });

  const discardAndReload = async (): Promise<void> => {
    if (editor.dirty && !(await askToDiscard())) {
      return;
    }
    await editor.reloadWorkspace();
  };

  return (
    <div className="space-y-4">
      {editor.behindStoredCopy ? (
        <Alert tone="info" data-testid="module-behind-notice">
          <span className="flex flex-wrap items-center gap-3">
            <span>
              Another editor saved this module. Your copy is out of date. Reload to see their
              changes, or keep working and resolve only the fields you both changed.
            </span>
            <Button
              variant="ghost"
              size="sm"
              icon="arrow-clockwise"
              loading={editor.reloading}
              loadingLabel="Reloading"
              disabled={editor.saveStatus === "saving"}
              disabledReason={editor.saveStatus === "saving" ? SAVING : undefined}
              onClick={async () => {
                // Nothing typed is lost by a clean reload, so only a dirty one asks.
                if (!editor.dirty || (await askToDiscard())) {
                  editor.reloadWorkspace();
                }
              }}
            >
              Reload
            </Button>
          </span>
        </Alert>
      ) : null}
      {editor.moduleConflict ? (
        // Its "Use theirs" writes the draft, so it locks with the panels below.
        <EditLockProvider decision={moduleEditDecision(editor)} lease={moduleLeaseRefusal(editor)}>
          <ModuleConflictPanel
            mine={moduleDraft}
            theirs={editor.moduleConflict.currentModule}
            base={record.module}
            onTakeTheirs={(key, value) =>
              editor.updateDraft((draft) => {
                if (value === undefined) {
                  delete draft[key];
                } else {
                  draft[key] = value;
                }
              })
            }
            myMappings={editor.ploToMimlos}
            theirMappings={editor.moduleConflict.currentPloToMimlos}
            baseMappings={record.ploToMimlos}
            onTakeTheirMappings={(taken, base) => {
              editor.setPloToMimlos(taken);
              editor.setRecord((current) =>
                current ? { ...current, ploToMimlos: base } : current,
              );
              editor.setSaveStatus("idle");
            }}
            onDismiss={() => {
              // Whatever the reader settled on, the next save composes over theirs.
              const theirs = editor.moduleConflict?.currentModule;
              editor.setRecord((current) =>
                current
                  ? { ...current, module: rebaseOnto(current.module, theirs, moduleDraft) }
                  : current,
              );
              editor.setModuleConflict(null);
            }}
          />
        </EditLockProvider>
      ) : null}
      {editor.saveError ? (
        <div data-testid="module-workspace-save-error">
          <Alert tone="warning">{editor.saveError.message}</Alert>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              icon="arrow-clockwise"
              disabled={!editor.canEdit || !editor.dirty || editor.saveStatus === "saving"}
              disabledReason={retryRefusal(editor)}
              onClick={editor.save}
            >
              Retry save
            </Button>
            <Button
              variant="outline"
              icon="arrow-clockwise"
              loading={editor.reloading}
              loadingLabel="Reloading"
              disabled={editor.saveStatus === "saving"}
              disabledReason={editor.saveStatus === "saving" ? SAVING : undefined}
              onClick={discardAndReload}
            >
              Discard mine and reload
            </Button>
          </div>
        </div>
      ) : null}
      {editor.reviewActionError ? (
        <Alert tone="warning">{editor.reviewActionError.message}</Alert>
      ) : null}
      {review.status === "Submitted for Review" ? (
        <div>
          <Alert tone="info">
            Submitted for programme lead review
            {review.submittedAt ? (
              <>
                {" on "}
                <Time value={review.submittedAt} variant="date" />
              </>
            ) : null}
            . The submitted module is read only until a decision is recorded.
          </Alert>
          {record.latestSubmission?.submissionNote ? (
            <Panel density="compact" headingLevel={3} title="Builder note" className="mt-3">
              <MutedText>{record.latestSubmission.submissionNote}</MutedText>
            </Panel>
          ) : null}
          {(record.latestSubmission?.warnings.length ?? 0) > 0 ? (
            <Alert
              tone="warning"
              role="none"
              className="mt-3"
              data-testid="module-submission-warnings"
            >
              <Heading level={3} size="md">
                Warnings recorded at submission
              </Heading>
              <ul className="mt-2 list-disc space-y-1 ps-5">
                {record.latestSubmission?.warnings.map((entry) => (
                  <li key={entry.code}>{entry.message}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
        </div>
      ) : null}
      {review.status === "Changes Requested" ? (
        // Requesting changes, returning with suggestions and refusing approval
        // all record this one status, so the note is what says which it was.
        <Alert tone="warning" data-testid="module-review-returned">
          The programme lead returned this module
          {review.reviewNote ? `: ${review.reviewNote}` : "."}
        </Alert>
      ) : null}
      {review.status === "Approved" ? (
        <Alert tone="success">
          This module has been approved by the programme lead and is now read only.
        </Alert>
      ) : null}
      {editor.leaseError ? (
        <Alert tone="warning">
          <span className="flex flex-wrap items-center gap-3">
            <span>{editor.leaseError.message} This module is open read only.</span>
            <Button
              variant="ghost"
              size="sm"
              loading={editor.leaseAcquiring}
              loadingLabel="Taking the module lease"
              onClick={() => editor.retryLease()}
            >
              Try again
            </Button>
          </span>
        </Alert>
      ) : null}
      {!editor.canEdit && !editor.leaseError ? (
        <Alert tone="info">
          <span className="flex flex-wrap items-center gap-3">
            <span>{readOnlyReason(editor)}</span>
            {editor.handedBack && editor.record?.canEdit ? (
              <Button
                variant="ghost"
                size="sm"
                loading={editor.leaseAcquiring}
                loadingLabel="Taking the module lease"
                onClick={() => editor.retryLease()}
              >
                Edit it yourself
              </Button>
            ) : null}
          </span>
        </Alert>
      ) : null}
    </div>
  );
}

export default ModuleNotices;
